// components/island/deck/TelefonPage.tsx
// Telefon-Seite der Pille: eine Eingabezeile deutet Name/Nummer (dialerEingabe.ts),
// darunter je nach Deutung Wähl-Zeile/Treffer oder die Anrufliste; Ziffernblock im
// iOS-Stil ab 700 px daneben, schmaler per Umschalter darunter (Handoff Pille §5, Screen 14).
//
// Rückmeldung übers Wählen läuft NICHT über useIslandActions().notify (das morpht
// den Modus nur aus idle/live heraus zu 'toast' — im offenen Deck bleibt der Modus
// 'deck', das Toast-Feld wird zwar gesetzt, aber nirgends gerendert). useNotification()
// .showToast WÄRE dagegen sichtbar gewesen (globaler Toast auf z-[500], über der Pille
// auf z-50) — hier trotzdem bewusst NICHT verwendet: ein Inline-Banner sitzt direkt bei
// der Eingabe/den Ergebnissen, statt als Overlay irgendwo am Bildschirmrand aufzupoppen,
// und bleibt stehen, bis der Mensch ihn wegklickt oder die nächste Aktion ihn ersetzt —
// bei einer Fehlermeldung zum Wählweg (z. B. "Konto nicht verbunden" mit Profil-Link)
// ist das kontextnäher als ein 3,8 Sekunden lang aufblitzender Toast. Dasselbe
// Inline-Muster wie ChatPage.tsx es für seine Fehlermeldung nutzt.
import { useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import {
  Backspace, DotsNine, MagnifyingGlass, Phone, PhoneIncoming, PhoneOutgoing, PhoneX, SpinnerGap, User, X, XCircle,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { supabase, isDemoMode } from '../../../lib/supabase/client';
import { istTelefonieVerbunden } from '../../../lib/telefonie/status';
import { deuteEingabe } from '../../../lib/telefonie/dialerEingabe';
import { zuListenEintrag, zeitKurz, type ListenEintrag } from '../../../lib/telefonie/anrufListe';
import { ROLLE, type AnrufZeile, type AnrufZiel } from '../../../lib/telefonie/anrufKarte';
import { anrufZielPfad } from '../../../lib/telefonie/anrufZiel';
import { waehleTreffer, type Kandidat } from '../../../lib/telefonie/treffer';
import { useIslandActions } from '../IslandProvider';
import { FARBE } from '../pille-stil';
import { figgoSignal } from '../figgoSignal';
import { useViewport } from '../hooks';

/** Stoppt die Ereignis-Weitergabe nur für Enter/Leertaste — der Hörer-Knopf sitzt
 *  in einer klickbaren Zeile (Kontakt öffnen), ohne den Stop würde ein Klick oder
 *  Enter/Space auf dem Knopf zusätzlich die Zeile aktivieren. Andere Tasten
 *  (Escape, ⌘K) müssen weiter zu den globalen window-Listenern der Pille durch —
 *  dieselbe Falle/Lösung wie in AnrufContent.tsx (stoppeAktivierungstasten). */
function stoppeAktivierungstasten(e: KeyboardEvent) {
  if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
}

/** Runder Rückruf-/Hörer-Knopf rechts in einer Zeile (Prototyp: 34 px, grün) — löst NUR
 *  den Anruf aus, nie die Zeile selbst (siehe stoppeAktivierungstasten). `name` fließt ins
 *  aria-label, damit Screenreader-Nutzer hören, wen sie anrufen. */
function HoererKnopf({
  nummer, name, waehlt, onWaehlen,
}: {
  nummer: string;
  name: string;
  waehlt: string | null;
  onWaehlen: (nummer: string) => void;
}) {
  const laedt = waehlt === nummer;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onWaehlen(nummer); }}
      onKeyDown={stoppeAktivierungstasten}
      disabled={laedt}
      aria-label={`${name} anrufen`}
      title="Rückruf"
      className="pille-druck flex shrink-0 cursor-pointer items-center justify-center rounded-full"
      style={{ width: 34, height: 34, border: 'none', background: 'rgba(95,211,138,0.16)', color: '#5fd38a', opacity: laedt ? 0.6 : 1 }}
    >
      {laedt ? <SpinnerGap size={15} className="pille-dreh" /> : <Phone size={15} weight="fill" />}
    </button>
  );
}

/** Zeilen-Rahmen für Suchtreffer/Anrufliste — die Zeile selbst wählt nicht
 *  (das erledigt der HoererKnopf), sondern öffnet den erkannten Kontakt. Bewusst
 *  ein div statt eines button: die Zeile trägt einen echten Hörer-Knopf als Kind,
 *  Button-in-Button ist ungültiges HTML.
 *  Ohne onOeffnen (kein Kontakt erkannt) ist die Zeile gar kein Knopf mehr. */
function ZeilenRahmen({ onOeffnen, children }: { onOeffnen: (() => void) | null; children: ReactNode }) {
  const stil: CSSProperties = { minHeight: 50, padding: '6px 8px', borderRadius: 13, gap: 10 };
  if (!onOeffnen) {
    return <div className="flex w-full items-center" style={stil}>{children}</div>;
  }
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOeffnen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOeffnen(); }
      }}
      className="pille-telzeile flex w-full cursor-pointer items-center text-left"
      style={stil}
    >
      {children}
    </div>
  );
}

/** Leer-/Ladezustand mittig in der Liste. */
function Hinweis({ icon, text }: { icon: ReactNode; text: string }) {
  return (
    <div className="flex h-full flex-col items-center justify-center text-center" style={{ gap: 8, padding: '24px 0', color: 'rgba(235,235,245,0.45)' }}>
      {icon}
      <span style={{ fontSize: 13, fontWeight: 600 }}>{text}</span>
    </div>
  );
}

interface SucheTreffer {
  owner_typ: string;
  owner_id: string;
  anzeigename: string | null;
  nummer_e164: string;
  label: string | null;
  objekt_id: string | null;
}

const TASTEN = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '+', '0', '⌫'] as const;
/** Buchstaben unter den Ziffern wie beim iPhone-Ziffernblock (nur Beschriftung). */
const BUCHSTABEN: Partial<Record<(typeof TASTEN)[number], string>> = {
  '2': 'ABC', '3': 'DEF', '4': 'GHI', '5': 'JKL', '6': 'MNO', '7': 'PQRS', '8': 'TUV', '9': 'WXYZ',
};

export function TelefonPage() {
  const { collapse, onSearchNavigate } = useIslandActions();
  const navigate = useNavigate();
  const [eingabe, setEingabe] = useState('');
  const [tastaturOffen, setTastaturOffen] = useState(false);
  const [anrufe, setAnrufe] = useState<AnrufZeile[]>([]);
  const [sucheTreffer, setSucheTreffer] = useState<SucheTreffer[]>([]);
  const [sucheLaedt, setSucheLaedt] = useState(false);
  const [nummerKandidaten, setNummerKandidaten] = useState<Kandidat[]>([]);
  const [meldung, setMeldung] = useState<{ ton: 'info' | 'error'; text: string; nichtVerbunden?: boolean } | null>(null);
  const [waehlt, setWaehlt] = useState<string | null>(null);
  const [jetzt] = useState(() => Date.now());
  /** Ab 700 px steht der Ziffernblock immer daneben (Prototyp), schmaler per Umschalter. */
  const breit = useViewport().width >= 700;

  const eingedeutet = useMemo(() => deuteEingabe(eingabe), [eingabe]);
  const nummerWahl = useMemo(() => waehleTreffer(nummerKandidaten), [nummerKandidaten]);
  // Aktuell wählbare Nummer für Enter im Eingabefeld: nur bei eindeutiger Ziffernfolge.
  const eingabeE164 = eingedeutet.art === 'nummer' ? eingedeutet.e164 : null;

  // Anrufliste laden + live halten — abgelöste Warteschlangen-Beine bleiben schon
  // per Filter draußen, nicht erst client-seitig herausgesiebt. Im Demo-Modus keine
  // echte Abfrage/Kanal (Vorbild: useAnrufe.ts/useVerpassteAnrufe.ts).
  useEffect(() => {
    if (isDemoMode()) return;
    let abgebrochen = false;
    async function laden() {
      const { data, error } = await supabase
        .from('anrufe')
        .select('*')
        .is('abgeloest_von', null)
        .order('begonnen_am', { ascending: false })
        .limit(30);
      if (abgebrochen) return;
      if (error) { setMeldung({ ton: 'error', text: 'Anrufliste konnte nicht geladen werden.' }); return; }
      setAnrufe((data as AnrufZeile[]) ?? []);
    }
    void laden();
    const kanal = supabase
      .channel('anrufe-telefon-seite')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'anrufe' }, () => void laden())
      .subscribe();
    return () => {
      abgebrochen = true;
      void supabase.removeChannel(kanal);
    };
  }, []);

  // Namenssuche — mit kurzer Verzögerung, damit nicht jeder Tastenanschlag eine
  // eigene Abfrage auslöst. `sucheLaedt` haelt fest, ob wirklich fertig gesucht wurde:
  // sonst liest der Mensch zwischen Entprellung und Antwort-Roundtrip "nichts gefunden",
  // obwohl die Suche noch läuft — bei jedem weiteren Tastendruck erneut.
  useEffect(() => {
    if (eingedeutet.art !== 'suche') { setSucheTreffer([]); setSucheLaedt(false); return; }
    if (isDemoMode()) { setSucheTreffer([]); setSucheLaedt(false); return; }
    let abgebrochen = false;
    setSucheLaedt(true);
    const timer = setTimeout(async () => {
      const { data, error } = await supabase.rpc('suche_telefon_kontakte', { p_text: eingedeutet.begriff });
      if (abgebrochen) return;
      if (error) {
        setMeldung({ ton: 'error', text: 'Suche konnte nicht ausgeführt werden.' });
        setSucheTreffer([]);
      } else {
        setSucheTreffer((data as SucheTreffer[]) ?? []);
      }
      setSucheLaedt(false);
    }, 250);
    return () => { abgebrochen = true; clearTimeout(timer); };
  }, [eingedeutet]);

  // Kontakt-Treffer zur eingetippten Nummer — dieselbe Sicht/Rangfolge wie beim
  // eingehenden Anruf (finde_zu_nummer + treffer.ts), damit ein und derselbe Mensch
  // mit mehreren Rollen hier nicht mehrfach auftaucht.
  useEffect(() => {
    if (eingedeutet.art !== 'nummer' || !eingedeutet.e164) { setNummerKandidaten([]); return; }
    if (isDemoMode()) { setNummerKandidaten([]); return; }
    let abgebrochen = false;
    void (async () => {
      const { data, error } = await supabase.rpc('finde_zu_nummer', { p_e164: eingedeutet.e164 });
      if (abgebrochen) return;
      if (error) { setMeldung({ ton: 'error', text: 'Kontakt-Treffer konnten nicht geladen werden.' }); return; }
      setNummerKandidaten((data as Kandidat[]) ?? []);
    })();
    return () => { abgebrochen = true; };
  }, [eingedeutet]);

  async function waehle(e164: string | null) {
    if (!e164 || waehlt) return;
    setMeldung(null);
    setWaehlt(e164);
    try {
      const verbunden = await istTelefonieVerbunden();
      if (!verbunden) {
        setMeldung({ ton: 'error', text: 'Kein Webex-Konto verbunden.', nichtVerbunden: true });
        return;
      }
      const { error } = await supabase.functions.invoke('telefonie-dial', { body: { nummer: e164 } });
      if (error) throw error;
      // Wortlaut wie in TelefonLink.tsx: „Dein Telefon" war irreführend, das Endgerät ist
      // bei uns die Webex-App auf dem Rechner, kein Apparat.
      setMeldung({ ton: 'info', text: 'Webex klingelt gleich — dort annehmen, dann wird gewählt.' });
    } catch {
      setMeldung({ ton: 'error', text: 'Wählen über die Telefonanlage fehlgeschlagen.' });
    } finally {
      setWaehlt(null);
    }
  }

  // Zeile öffnet den Kontakt, statt zu wählen — dasselbe Muster wie
  // AnrufContent.tsx/oeffneKontakt: Pille schließen, dann navigieren.
  function oeffneKontakt(ziel: AnrufZiel) {
    const pfad = anrufZielPfad(ziel);
    if (!pfad) return;
    collapse();
    if (onSearchNavigate) onSearchNavigate(pfad, ziel.id, ziel.typ);
    else navigate(pfad);
  }

  // Figgo wählt mit, sobald die erste Ziffer steht (Prototyp: p .5, Abkühlung 5 s)
  const eingabeVorher = useRef('');
  useEffect(() => {
    if (eingabe.length === 1 && eingabeVorher.current === '') figgoSignal('tel', { p: 0.5, cool: 5000 });
    eingabeVorher.current = eingabe;
  }, [eingabe]);

  function taste(z: string) {
    setEingabe((s) => (z === '⌫' ? s.slice(0, -1) : s + z));
  }

  // Enter im Eingabefeld: bei einer wählbaren Nummer sofort anrufen — der Reflex bei
  // jedem Dialer. Bei Suche/zu-kurzer-Nummer passiert nichts (keine eindeutige Aktion).
  function eingabeEnter(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter') return;
    if (eingabeE164) void waehle(eingabeE164);
  }

  const tastatur = (
    <div className="grid justify-center" style={{ gridTemplateColumns: 'repeat(3, 64px)', gap: 12, alignContent: 'center' }}>
      {TASTEN.map((z) => (
        <button
          key={z}
          type="button"
          onClick={() => taste(z)}
          aria-label={z === '⌫' ? 'Löschen' : z}
          className="pille-taste flex cursor-pointer flex-col items-center justify-center rounded-full"
          style={{ width: 64, height: 64, border: 'none', fontFamily: 'inherit', color: '#fff' }}
        >
          {z === '⌫' ? <Backspace size={24} /> : (
            <>
              <span style={{ fontSize: 24, fontWeight: 600, lineHeight: 1 }}>{z}</span>
              <span aria-hidden style={{ height: 10, fontSize: 8, fontWeight: 800, letterSpacing: '0.12em', color: 'rgba(255,255,255,0.55)' }}>{BUCHSTABEN[z] ?? ''}</span>
            </>
          )}
        </button>
      ))}
    </div>
  );

  return (
    <div className="flex h-full text-white" style={{ gap: 14, padding: '0 14px 14px' }}>
      <div className="flex min-w-0 flex-1 flex-col" style={{ gap: 8 }}>
        {/* Eingabezeile */}
        <div className="pille-feld flex shrink-0 items-center" style={{ height: 44, borderRadius: 14, gap: 8, padding: '0 6px 0 14px', background: 'rgba(255,255,255,0.08)' }}>
          <MagnifyingGlass size={16} color="rgba(255,255,255,0.5)" aria-hidden />
          <input
            value={eingabe}
            onChange={(e) => setEingabe(e.target.value)}
            onKeyDown={eingabeEnter}
            placeholder="Suche …"
            aria-label="Name oder Nummer"
            className="min-w-0 flex-1 bg-transparent text-white placeholder:text-[rgba(235,235,245,0.45)]"
            style={{ border: 'none', outline: 'none', fontFamily: 'inherit', fontSize: 15, fontWeight: 700, letterSpacing: '0.02em' }}
            autoComplete="off"
            spellCheck={false}
          />
          {eingabe && (
            <button
              type="button"
              onClick={() => setEingabe('')}
              aria-label="Eingabe löschen"
              className="flex shrink-0 cursor-pointer items-center justify-center rounded-full"
              style={{ width: 28, height: 28, border: 'none', background: 'transparent', color: 'rgba(255,255,255,0.5)' }}
            >
              <XCircle size={18} weight="fill" />
            </button>
          )}
          {!breit && <button
            type="button"
            onClick={() => setTastaturOffen((v) => !v)}
            aria-label="Zifferntastatur ein-/ausblenden"
            aria-pressed={tastaturOffen}
            className="flex shrink-0 cursor-pointer items-center justify-center"
            style={{ width: 32, height: 32, borderRadius: 10, border: 'none', background: tastaturOffen ? 'rgba(226,192,141,0.2)' : 'transparent', color: tastaturOffen ? FARBE.gold : 'rgba(255,255,255,0.5)' }}
          >
            <DotsNine size={18} weight="bold" />
          </button>}
        </div>

        {/* Rückmeldung übers Wählen */}
        {meldung && (
          <div
            role={meldung.ton === 'error' ? 'alert' : 'status'}
            className="flex shrink-0 items-start"
            style={{
              gap: 8, padding: '8px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
              background: meldung.ton === 'error' ? 'rgba(255,69,58,0.14)' : 'rgba(226,192,141,0.14)',
              color: meldung.ton === 'error' ? '#ff8a80' : FARBE.gold,
            }}
          >
            <span className="min-w-0 flex-1">
              {meldung.text}
              {meldung.nichtVerbunden && (
                <>
                  {' '}
                  <button
                    type="button"
                    onClick={() => { collapse(); onSearchNavigate?.('/admin/profile', '', ''); }}
                    className="cursor-pointer underline underline-offset-2 hover:text-white"
                    style={{ border: 'none', background: 'transparent', color: 'inherit', font: 'inherit', padding: 0 }}
                  >
                    Im Profil verbinden
                  </button>
                </>
              )}
            </span>
            <button
              type="button"
              onClick={() => setMeldung(null)}
              aria-label="Meldung schließen"
              className="shrink-0 cursor-pointer opacity-60 hover:opacity-100"
              style={{ border: 'none', background: 'transparent', color: 'inherit', padding: 0 }}
            >
              <X size={12} weight="bold" />
            </button>
          </div>
        )}

        {/* Inhalt je nach Eingabe */}
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto" style={{ gap: 2 }}>
          {eingedeutet.art === 'nummer' ? (
            <>
              {eingedeutet.e164 ? (
                <button
                  type="button"
                  onClick={() => void waehle(eingedeutet.e164)}
                  disabled={waehlt === eingedeutet.e164}
                  className="pille-druck flex w-full shrink-0 cursor-pointer items-center"
                  style={{ minHeight: 46, padding: '0 12px', borderRadius: 14, gap: 10, border: 'none', background: 'rgba(95,211,138,0.16)', color: '#5fd38a', fontFamily: 'inherit', opacity: waehlt === eingedeutet.e164 ? 0.6 : 1 }}
                >
                  {waehlt === eingedeutet.e164 ? <SpinnerGap size={17} className="pille-dreh" /> : <PhoneOutgoing size={17} weight="fill" />}
                  <span className="flex-1 truncate text-left" style={{ fontSize: 14, fontWeight: 800 }}>{eingedeutet.e164} anrufen</span>
                </button>
              ) : (
                <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', padding: '0 4px' }}>Noch zu kurz zum Wählen</span>
              )}
              {nummerWahl.treffer && (
                <div className="flex items-center" style={{ gap: 8, padding: '6px 12px 6px 39px' }}>
                  <span className="min-w-0 flex-1 truncate" style={{ fontSize: 13, fontWeight: 700 }}>
                    {(nummerWahl.treffer.anzeigename || '').trim() || 'Ohne Namen'}
                  </span>
                  <span className="shrink-0" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>
                    {ROLLE[nummerWahl.treffer.owner_typ] ?? nummerWahl.treffer.owner_typ}
                  </span>
                </div>
              )}
              {nummerWahl.kandidaten?.map((k) => (
                <div key={`${k.owner_typ}:${k.owner_id}`} className="flex items-center" style={{ gap: 8, padding: '6px 12px 6px 39px' }}>
                  <span className="min-w-0 flex-1 truncate" style={{ fontSize: 13, fontWeight: 700 }}>
                    {(k.anzeigename || '').trim() || 'Ohne Namen'}
                  </span>
                  <span className="shrink-0" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>{ROLLE[k.owner_typ] ?? k.owner_typ}</span>
                </div>
              ))}
            </>
          ) : eingedeutet.art === 'suche' ? (
            sucheLaedt ? (
              <Hinweis icon={<SpinnerGap size={22} className="pille-dreh" />} text="Suche …" />
            ) : sucheTreffer.length === 0 ? (
              <Hinweis icon={<MagnifyingGlass size={24} />} text={`Nichts gefunden für „${eingedeutet.begriff}"`} />
            ) : (
              sucheTreffer.map((t) => {
                const ziel: AnrufZiel = { typ: t.owner_typ, id: t.owner_id };
                const pfad = anrufZielPfad(ziel);
                const name = (t.anzeigename || '').trim() || 'Ohne Namen';
                return (
                  <ZeilenRahmen
                    key={`${t.owner_typ}:${t.owner_id}:${t.nummer_e164}`}
                    onOeffnen={pfad ? () => oeffneKontakt(ziel) : null}
                  >
                    <User size={16} weight="fill" color="rgba(255,255,255,0.5)" aria-hidden />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate" style={{ fontSize: 14, fontWeight: 700 }}>{name}</span>
                      <span className="truncate" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>
                        {[ROLLE[t.owner_typ] ?? t.owner_typ, t.label].filter(Boolean).join(' · ')} · {t.nummer_e164}
                      </span>
                    </span>
                    <HoererKnopf nummer={t.nummer_e164} name={name} waehlt={waehlt} onWaehlen={(n) => void waehle(n)} />
                  </ZeilenRahmen>
                );
              })
            )
          ) : (
            <AnrufListe
              anrufe={anrufe}
              jetzt={jetzt}
              waehlt={waehlt}
              onRueckruf={(n) => void waehle(n)}
              onOeffnen={oeffneKontakt}
            />
          )}
        </div>

        {/* Ziffernblock schmal: per Umschalter unter der Liste */}
        {!breit && tastaturOffen && <div className="shrink-0" style={{ paddingTop: 6 }}>{tastatur}</div>}
      </div>

      {/* Ziffernblock breit: immer daneben (Prototyp ab 700 px) */}
      {breit && <div className="flex shrink-0" style={{ width: 216 }}>{tastatur}</div>}
    </div>
  );
}

function AnrufListe({
  anrufe, jetzt, waehlt, onRueckruf, onOeffnen,
}: {
  anrufe: AnrufZeile[];
  jetzt: number;
  waehlt: string | null;
  onRueckruf: (nummer: string) => void;
  onOeffnen: (ziel: AnrufZiel) => void;
}) {
  if (anrufe.length === 0) {
    return <Hinweis icon={<Phone size={24} />} text="Noch keine Anrufe" />;
  }
  return (
    <>
      {anrufe.map((a) => {
        const zeile = zuListenEintrag(a);
        return (
          <AnrufZeileRow key={zeile.id} zeile={zeile} jetzt={jetzt} waehlt={waehlt} onRueckruf={onRueckruf} onOeffnen={onOeffnen} />
        );
      })}
    </>
  );
}

function AnrufZeileRow({
  zeile, jetzt, waehlt, onRueckruf, onOeffnen,
}: {
  zeile: ListenEintrag;
  jetzt: number;
  waehlt: string | null;
  onRueckruf: (nummer: string) => void;
  onOeffnen: (ziel: AnrufZiel) => void;
}) {
  const RichtungIcon = zeile.verpasst ? PhoneX : zeile.richtung === 'ausgehend' ? PhoneOutgoing : PhoneIncoming;
  const unter = [zeile.rolle, zeile.dauerText].filter(Boolean).join(' · ');
  return (
    <ZeilenRahmen onOeffnen={zeile.ziel ? () => onOeffnen(zeile.ziel as AnrufZiel) : null}>
      <RichtungIcon size={16} weight="fill" color={zeile.verpasst ? '#ff7b72' : 'rgba(255,255,255,0.5)'} aria-label={zeile.verpasst ? 'verpasst' : zeile.richtung} />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate" style={{ fontSize: 14, fontWeight: 700, color: zeile.verpasst ? '#ff7b72' : '#fff' }}>{zeile.titel}</span>
        {unter && <span className="truncate tabular-nums" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>{unter}</span>}
      </span>
      <span className="shrink-0 whitespace-nowrap" style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{zeitKurz(zeile.begonnenAm, jetzt)}</span>
      {zeile.rueckrufNummer && (
        <HoererKnopf nummer={zeile.rueckrufNummer} name={zeile.titel} waehlt={waehlt} onWaehlen={onRueckruf} />
      )}
    </ZeilenRahmen>
  );
}
